<template>
  <div>
    <h2>{{ $t("task.settings.title") }}</h2>
    <p class="text-muted">{{ $t("task.settings.hint") }}</p>

    <!-- TKP-181 md.84: salt okunur kullanıcı nedenini sayfanın başında görür (eskiden en altta küçük bir nottu) -->
    <b-alert v-if="!forbidden && loaded && !canEdit" show variant="warning" data-test="read-only"><i class="mdi mdi-lock-outline mr-1"></i>{{ $t("task.settings.read-only") }}</b-alert>

    <div class="card" v-if="!forbidden">
      <div class="card-body">
        <!-- TKP-220 (goreveposta.pdf): ayarlar sekmeli — E-posta, Durumlar, Öncelikler, Kategoriler, Görev Tipi -->
        <!-- TKP-239 g.1: sekmeler Görevler › Görevler görünümünde (nav-tab-menu) -->
        <b-tabs nav-class="nav-tab-menu mb-3" data-test="settings-tabs">
        <b-tab active data-test="tab-mail">
          <template v-slot:title>{{ $t("task.settings.mail.tab") }}</template>
            <task-mail-settings :mail="mail" :defaults="mailDefaults" :can-edit="canEdit" :is-updating="isUpdating" />
        </b-tab>
        <b-tab data-test="tab-statuses">
          <template v-slot:title>{{ $t("task.settings.statuses") }}</template>
            <h5 data-test="statuses-title">{{ $t("task.settings.statuses") }}</h5>
            <table class="table table-sm align-middle" data-test="statuses">
              <thead>
                <tr>
                  <th style="width: 6rem">{{ $t("task.settings.order") }}</th>
                  <th>{{ $t("common.name") }}</th>
                  <th style="width: 14rem">{{ $t("task.settings.kind") }}</th>
                  <th style="width: 8rem">{{ $t("task.settings.color") }}</th>
                  <th style="width: 14rem"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in statusRows" :key="row._id" :data-test="`status-${row.key}`">
                  <td><input type="number" class="form-control form-control-sm" v-model.number="row.order" :disabled="!canEdit" /></td>
                  <td><input type="text" class="form-control form-control-sm" v-model="row.label" :disabled="!canEdit" /></td>
                  <td>
                    <select class="form-control form-control-sm" v-model="row.kind" :disabled="!canEdit || row.system" :title="row.system ? $t('task.settings.system-locked') : ''">
                      <option v-for="kind in kinds" :key="kind" :value="kind">{{ $t(`task.settings.kinds.${kind}`) }}</option>
                    </select>
                  </td>
                  <td><input type="color" class="form-control form-control-sm" v-model="row.color" :disabled="!canEdit" /></td>
                  <td class="text-right">
                    <b-badge v-if="row.system" variant="light" class="mr-2">{{ $t("task.settings.system") }}</b-badge>
                    <sweet-button v-if="canEdit" @click="saveStatus(row)" :loading="isUpdating" variant="success" icon="mdi mdi-check" size="sm" data-test="save-status">{{ $t("common.save") }}</sweet-button>
                    <sweet-button v-if="canEdit && isDeletableStatus(row)" @click="removeStatus(row)" :loading="isUpdating" variant="danger" icon="bx bx-trash" size="sm" buttonClass="ml-1" data-test="delete-status">{{ $t("common.delete") }}</sweet-button>
                  </td>
                </tr>
                <tr v-if="canEdit" data-test="new-status">
                  <td></td>
                  <td><input type="text" class="form-control form-control-sm" :class="{ 'is-invalid': missing === 'status' }" ref="new-status-name" v-model="newStatus.label" @input="missing = null" @keyup.enter="addStatus()" :placeholder="$t('task.settings.new-status')" data-test="new-status-name" /></td>
                  <td>
                    <select class="form-control form-control-sm" v-model="newStatus.kind" data-test="new-status-kind">
                      <option v-for="kind in kinds" :key="kind" :value="kind">{{ $t(`task.settings.kinds.${kind}`) }}</option>
                    </select>
                  </td>
                  <td><input type="color" class="form-control form-control-sm" v-model="newStatus.color" /></td>
                  <td class="text-right">
                    <sweet-button @click="addStatus()" :loading="isUpdating" variant="primary" icon="bx bx-plus" size="sm" data-test="add-status">{{ $t("common.add") }}</sweet-button>
                  </td>
                </tr>
              </tbody>
            </table>
        </b-tab>
        <b-tab data-test="tab-priorities">
          <template v-slot:title>{{ $t("task.settings.priorities") }}</template>
            <h5 data-test="priorities-title">{{ $t("task.settings.priorities") }}</h5>
            <table class="table table-sm align-middle" data-test="priorities">
              <thead>
                <tr>
                  <th style="width: 6rem">{{ $t("task.settings.order") }}</th>
                  <th>{{ $t("common.name") }}</th>
                  <th style="width: 8rem">{{ $t("task.settings.color") }}</th>
                  <th style="width: 14rem"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in priorityRows" :key="row._id" :data-test="`priority-${row.value}`">
                  <td><input type="number" class="form-control form-control-sm" v-model.number="row.order" :disabled="!canEdit" /></td>
                  <td><input type="text" class="form-control form-control-sm" v-model="row.label" :disabled="!canEdit" /></td>
                  <td><input type="color" class="form-control form-control-sm" v-model="row.color" :disabled="!canEdit" /></td>
                  <td class="text-right">
                    <b-badge v-if="row.system" variant="light" class="mr-2">{{ $t("task.settings.system") }}</b-badge>
                    <sweet-button v-if="canEdit" @click="savePriority(row)" :loading="isUpdating" variant="success" icon="mdi mdi-check" size="sm" data-test="save-priority">{{ $t("common.save") }}</sweet-button>
                    <sweet-button v-if="canEdit && !row.system" @click="removePriority(row)" :loading="isUpdating" variant="danger" icon="bx bx-trash" size="sm" buttonClass="ml-1" data-test="delete-priority">{{ $t("common.delete") }}</sweet-button>
                  </td>
                </tr>
                <tr v-if="canEdit" data-test="new-priority">
                  <td></td>
                  <td><input type="text" class="form-control form-control-sm" :class="{ 'is-invalid': missing === 'priority' }" ref="new-priority-name" v-model="newPriority.label" @input="missing = null" @keyup.enter="addPriority()" :placeholder="$t('task.settings.new-priority')" data-test="new-priority-name" /></td>
                  <td><input type="color" class="form-control form-control-sm" v-model="newPriority.color" /></td>
                  <td class="text-right">
                    <sweet-button @click="addPriority()" :loading="isUpdating" variant="primary" icon="bx bx-plus" size="sm" data-test="add-priority">{{ $t("common.add") }}</sweet-button>
                  </td>
                </tr>
              </tbody>
            </table>
        </b-tab>
        <b-tab data-test="tab-categories">
          <template v-slot:title>{{ $t("task.settings.categories") }}</template>
            <!-- TKP-168: kategoriler organizasyon bazında (TKP-119 "kütüphanede kalır" kararı geri alındı); sistem kaydı yok -->
            <h5 data-test="categories-title">{{ $t("task.settings.categories") }}</h5>
            <table class="table table-sm align-middle" data-test="categories">
              <thead>
                <tr>
                  <th style="width: 6rem">{{ $t("task.settings.order") }}</th>
                  <th>{{ $t("common.name") }}</th>
                  <th style="width: 14rem"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in categoryRows" :key="row._id" :data-test="`category-${row._id}`">
                  <td><input type="number" class="form-control form-control-sm" v-model.number="row.order" :disabled="!canEdit" /></td>
                  <td><input type="text" class="form-control form-control-sm" v-model="row.label" :disabled="!canEdit" /></td>
                  <td class="text-right">
                    <sweet-button v-if="canEdit" @click="saveCategory(row)" :loading="isUpdating" variant="success" icon="mdi mdi-check" size="sm" data-test="save-category">{{ $t("common.save") }}</sweet-button>
                    <sweet-button v-if="canEdit" @click="removeCategory(row)" :loading="isUpdating" variant="danger" icon="bx bx-trash" size="sm" buttonClass="ml-1" data-test="delete-category">{{ $t("common.delete") }}</sweet-button>
                  </td>
                </tr>
                <tr v-if="!categoryRows.length"><td colspan="3" class="text-muted" data-test="categories-empty">{{ $t("task.settings.categories-empty") }}</td></tr>
                <tr v-if="canEdit" data-test="new-category">
                  <td></td>
                  <td><input type="text" class="form-control form-control-sm" :class="{ 'is-invalid': missing === 'category' }" ref="new-category-name" v-model="newCategory.label" @input="missing = null" @keyup.enter="addCategory()" :placeholder="$t('task.settings.new-category')" data-test="new-category-name" /></td>
                  <td class="text-right">
                    <sweet-button @click="addCategory()" :loading="isUpdating" variant="primary" icon="bx bx-plus" size="sm" data-test="add-category">{{ $t("common.add") }}</sweet-button>
                  </td>
                </tr>
              </tbody>
            </table>
        </b-tab>
        <b-tab data-test="tab-types">
          <template v-slot:title>{{ $t("task.settings.types") }}</template>
            <h5 data-test="types-title">{{ $t("task.settings.types") }}</h5>
            <!-- TKP-224: organizasyon bazında aç/kapa; kapalıyken görev formunda ve detayında tip görünmez (veri silinmez) -->
            <!-- TKP-239 g.4: anahtar bir punto büyük; kapalıyken tip listesi görünmez -->
            <b-form-checkbox switch size="lg" class="mb-1" data-test="type-enabled" :checked="typeEnabled" :disabled="!canEdit || isUpdating" @change="setTypeEnabled">
              {{ $t("task.settings.type-enabled") }}
            </b-form-checkbox>
            <p class="text-muted mb-3" data-test="type-enabled-hint">{{ $t("task.settings.type-enabled-hint") }}</p>
            <table v-if="typeEnabled" class="table table-sm align-middle" data-test="types">
              <thead>
                <tr>
                  <th style="width: 6rem">{{ $t("task.settings.order") }}</th>
                  <th>{{ $t("common.name") }}</th>
                  <th style="width: 14rem"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in typeRows" :key="row._id" :data-test="`type-${row._id}`">
                  <td><input type="number" class="form-control form-control-sm" v-model.number="row.order" :disabled="!canEdit" /></td>
                  <td><input type="text" class="form-control form-control-sm" v-model="row.label" :disabled="!canEdit" /></td>
                  <td class="text-right">
                    <sweet-button v-if="canEdit" @click="saveType(row)" :loading="isUpdating" variant="success" icon="mdi mdi-check" size="sm" data-test="save-type">{{ $t("common.save") }}</sweet-button>
                    <sweet-button v-if="canEdit" @click="removeType(row)" :loading="isUpdating" variant="danger" icon="bx bx-trash" size="sm" buttonClass="ml-1" data-test="delete-type">{{ $t("common.delete") }}</sweet-button>
                  </td>
                </tr>
                <tr v-if="!typeRows.length"><td colspan="3" class="text-muted" data-test="types-empty">{{ $t("task.settings.types-empty") }}</td></tr>
                <tr v-if="canEdit" data-test="new-type">
                  <td></td>
                  <td><input type="text" class="form-control form-control-sm" :class="{ 'is-invalid': missing === 'type' }" ref="new-type-name" v-model="newType.label" @input="missing = null" @keyup.enter="addType()" :placeholder="$t('task.settings.new-type')" data-test="new-type-name" /></td>
                  <td class="text-right">
                    <sweet-button @click="addType()" :loading="isUpdating" variant="primary" icon="bx bx-plus" size="sm" data-test="add-type">{{ $t("common.add") }}</sweet-button>
                  </td>
                </tr>
              </tbody>
            </table>
        </b-tab>
        </b-tabs>
      </div>
    </div>
    <div class="card" v-else><div class="card-body" data-test="forbidden">{{ $t("http.403") }}</div></div>
  </div>
</template>

<script>
import { STATUS_KINDS, isDeletableStatus, resolveCategories, resolvePriorities, resolveStatuses, resolveTypes } from "./task-settings";
import TaskMailSettings from "./task-mail-settings"; // TKP-220

/**
 * TKP-119 / tasarım s.25 md.1–2 — Görevler › Ayarlar. Durum ve öncelik organizasyon bazında kayıt; sistem varsayılanının
 * anahtarı/sınıfı/değeri kilitli (ad, renk, sıra düzenlenir); kullanımdaki değer silinemez (sunucu sayısıyla reddeder).
 * TKP-168: kategori de organizasyon bazında (ad + sıra; kullanımdaki silinemez, 409).
 */
export default {
  components: { TaskMailSettings },
  data() {
    return { forbidden: false, loaded: false, missing: null, kinds: STATUS_KINDS, statusRows: [], priorityRows: [], categoryRows: [], typeRows: [], newType: { label: "" }, newStatus: { label: "", kind: "OPEN", color: "#6c757d" }, newPriority: { label: "", color: "#6c757d" }, newCategory: { label: "" } };
  },
  computed: {
    canEdit() { return this.$store.getters["taskSettings/canEdit"]; },
    isUpdating() { return this.$store.getters["taskSettings/isUpdating"]; },
    language() { return this.$store.getters["user/account"]?.language ?? "tr"; },
    mail() { return this.$store.getters["taskSettings/mail"]; }, // TKP-220
    typeEnabled() { return this.$store.getters["taskSettings/typeEnabled"]; }, // TKP-224
    mailDefaults() { return this.$store.getters["taskSettings/mailDefaults"]; },
  },
  async mounted() {
    await this.load();
  },
  methods: {
    isDeletableStatus, // TKP-176 md.51: DONE/TODO dışındaki sistem durumları silinebilir (kullanımdaysa backend 409)
    async load() {
      try {
        await this.$store.dispatch("taskSettings/fetch");
        this.statusRows = resolveStatuses(this.$store.getters["taskSettings/statuses"], this.language, (k) => this.$t(k)).map((s) => ({ ...s, color: this.hex(s.color) }));
        this.priorityRows = resolvePriorities(this.$store.getters["taskSettings/priorities"], this.language, (k) => this.$t(k)).map((p) => ({ ...p, color: this.hex(p.color) }));
        this.categoryRows = resolveCategories(this.$store.getters["taskSettings/categories"], this.language);
        this.typeRows = resolveTypes(this.$store.getters["taskSettings/types"], this.language); // TKP-220
        this.loaded = true;
      } catch (error) {
        if (error?.response?.status === 403) this.forbidden = true;
        else this.$handleHttpErrorByStatusCode(error);
      }
    },
    /** `<input type="color">` yalnız #rrggbb kabul eder; adlandırılmış renkler (gray, red) sabit karşılığa çevrilir. */
    hex(color) {
      const named = { gray: "#808080", red: "#ff0000", "rgb(230, 107, 107)": "#e66b6b" };
      return /^#[0-9a-f]{6}$/i.test(color || "") ? color : named[color] || "#6c757d";
    },
    nameOf(row) { return { ...(typeof row.name === "object" && row.name ? row.name : {}), [this.language]: row.label }; },
    async act(fn) {
      try { await fn(); this.$toast.success(this.$t("common.success")); await this.load(); } catch (error) { this.$handleHttpErrorByStatusCode(error); }
    },
    saveStatus(row) { return this.act(() => this.$store.dispatch("taskSettings/updateStatus", { _id: row._id, name: this.nameOf(row), color: row.color, order: row.order, ...(row.system ? {} : { kind: row.kind }) })); },
    async removeStatus(row) { if (await this.confirm(this.$t("alert.approve"))) await this.act(() => this.$store.dispatch("taskSettings/deleteStatus", row._id)); },
    /** TKP-181 md.84: "Ekle" hep basılabilir; ad boşsa alan işaretlenir, odaklanır ve uyarı çıkar (eskiden tuş sessizce kapalıydı). */
    named(kind, label) {
      if (String(label || "").trim()) return true;
      this.missing = kind;
      this.$toast.warning(this.$t("task.settings.name-required"));
      const input = this.$refs[`new-${kind}-name`];
      if (input && input.focus) input.focus();
      return false;
    },
    addStatus() { if (!this.named("status", this.newStatus.label)) return; return this.act(async () => { await this.$store.dispatch("taskSettings/createStatus", { name: { [this.language]: this.newStatus.label }, kind: this.newStatus.kind, color: this.newStatus.color }); this.newStatus = { label: "", kind: "OPEN", color: "#6c757d" }; }); },
    savePriority(row) { return this.act(() => this.$store.dispatch("taskSettings/updatePriority", { _id: row._id, name: this.nameOf(row), color: row.color, order: row.order })); },
    async removePriority(row) { if (await this.confirm(this.$t("alert.approve"))) await this.act(() => this.$store.dispatch("taskSettings/deletePriority", row._id)); },
    saveCategory(row) { return this.act(() => this.$store.dispatch("taskSettings/updateCategory", { _id: row._id, name: this.nameOf(row), order: row.order })); },
    async removeCategory(row) { if (await this.confirm(this.$t("alert.approve"))) await this.act(() => this.$store.dispatch("taskSettings/deleteCategory", row._id)); },
    addCategory() { if (!this.named("category", this.newCategory.label)) return; return this.act(async () => { await this.$store.dispatch("taskSettings/createCategory", { name: { [this.language]: this.newCategory.label } }); this.newCategory = { label: "" }; }); },
    setTypeEnabled(typeEnabled) { return this.act(() => this.$store.dispatch("taskSettings/updateOptions", { typeEnabled: !!typeEnabled })); }, // TKP-224
    // TKP-220 — görev tipleri (kategoriyle aynı kurallar: ad zorunlu, kullanımdaki silinemez)
    saveType(row) { return this.act(() => this.$store.dispatch("taskSettings/updateType", { _id: row._id, name: this.nameOf(row), order: row.order })); },
    async removeType(row) { if (await this.confirm(this.$t("alert.approve"))) await this.act(() => this.$store.dispatch("taskSettings/deleteType", row._id)); },
    addType() { if (!this.named("type", this.newType.label)) return; return this.act(async () => { await this.$store.dispatch("taskSettings/createType", { name: { [this.language]: this.newType.label } }); this.newType = { label: "" }; }); },
    addPriority() { if (!this.named("priority", this.newPriority.label)) return; return this.act(async () => { await this.$store.dispatch("taskSettings/createPriority", { name: { [this.language]: this.newPriority.label }, color: this.newPriority.color }); this.newPriority = { label: "", color: "#6c757d" }; }); },
  },
};
</script>
